import { useRouter } from 'expo-router';
import { LayoutGrid, Plus, RefreshCw } from 'lucide-react-native';
import { useCallback, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { FadeIn } from '../../src/components/animations';
import { Box, EmptyState, HStack, Text, VStack } from '../../src/components/primitives';
import { OverviewBadge } from '../../src/components/primitives/BadgePresets';
import { Button } from '../../src/components/primitives/Button';
import { FormInput } from '../../src/components/primitives/FormInput';
import { IconButton } from '../../src/components/primitives/IconButton';
import { Sheet } from '../../src/components/primitives/Sheet';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { BoardViewToggle, KanbanBoard } from '../../src/features/leads';
import { StageMoveModal } from '../../src/features/leads/components/StageMoveModal';
import { useCurrentOrgRole } from '../../src/hooks/useAuthOrgRole';
import { useBoardRealtime } from '../../src/hooks/useBoardRealtime';
import { useForceQualifyLead } from '../../src/hooks/useJobTitleFilters';
import { useBoard, useFunnelTemplates, useMoveStage } from '../../src/hooks/useLeads';
import { colors } from '../../src/theme/colors';
import { tokens } from '../../src/theme/tokens';
import type { Lead } from '../../src/types/leads';

export default function FunnelScreen() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isMobile = width < tokens.breakpoints.tablet;
  const { currentOrganization, backendUser, getAccessToken } = useAuth();
  const { t } = useTranslation();

  const orgId = currentOrganization?.id;

  const { isConnected: realtimeConnected } = useBoardRealtime({
    organizationId: orgId,
    enabled: !!orgId,
    getAccessToken,
  });

  const { data: templates, isLoading: templatesLoading } = useFunnelTemplates(orgId);

  const activeTemplate = templates?.find((t) => t.isActive);

  const [viewMode, setViewMode] = useState<'my' | 'team'>('my');

  const {
    data: board,
    isLoading: boardLoading,
    error: boardError,
    refetch,
  } = useBoard(activeTemplate?.id, orgId, viewMode, realtimeConnected);

  const forceQualify = useForceQualifyLead();
  const { showError } = useToast();
  const role = useCurrentOrgRole();
  const isPrivileged = role === 'owner' || role === 'admin';

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showStageModal, setShowStageModal] = useState(false);
  const [blockedLead, setBlockedLead] = useState<Lead | null>(null);
  const [blockedTargetStageId, setBlockedTargetStageId] = useState<string | null>(null);
  const [showForceQualifyModal, setShowForceQualifyModal] = useState(false);
  const [forceQualifyReason, setForceQualifyReason] = useState('');

  const findLeadInBoard = useCallback(
    (leadId: string): Lead | undefined => {
      if (!board) return undefined;
      for (const stage of board.stages) {
        const found = stage.leads.find((l) => l.id === leadId);
        if (found) return found;
      }
      return undefined;
    },
    [board],
  );

  const handleLeadPress = useCallback(
    (lead: Lead) => {
      if (
        backendUser &&
        board?.visibility?.isReadOnly &&
        lead.assignedTo &&
        lead.assignedTo !== backendUser.id
      ) {
        showError(t('leads.viewMode.readOnly'));
        return;
      }
      router.push(`/lead/${lead.id}`);
    },
    [router.push, board?.visibility?.isReadOnly, backendUser, showError, t],
  );

  const openForceQualifyForLead = useCallback((lead: Lead, targetStageId: string) => {
    setBlockedLead(lead);
    setBlockedTargetStageId(targetStageId);
    setShowForceQualifyModal(true);
    setForceQualifyReason('');
  }, []);

  const moveStage = useMoveStage({
    onQualificationBlocked: ({ variables }) => {
      if (!isPrivileged) return false;
      const lead = findLeadInBoard(variables.leadId);
      if (!lead) return false;
      openForceQualifyForLead(lead, variables.stageId);
      return true;
    },
  });

  const handleForceQualifySubmit = useCallback(() => {
    if (!blockedLead || !forceQualifyReason.trim()) return;
    const leadId = blockedLead.id;
    const targetStageId = blockedTargetStageId;
    forceQualify.mutate(
      { leadId, reason: forceQualifyReason.trim() },
      {
        onSuccess: () => {
          setShowForceQualifyModal(false);
          setBlockedLead(null);
          setBlockedTargetStageId(null);
          setForceQualifyReason('');
          if (targetStageId) {
            moveStage.mutate({ leadId, stageId: targetStageId });
          }
        },
        onError: () => {},
      },
    );
  }, [blockedLead, blockedTargetStageId, forceQualifyReason, forceQualify, moveStage.mutate]);

  const handleMoveStage = useCallback(
    (stageId: string) => {
      if (!selectedLead) return;
      moveStage.mutate({ leadId: selectedLead.id, stageId });
      setShowStageModal(false);
      setSelectedLead(null);
    },
    [selectedLead, moveStage.mutate],
  );

  const handleDragDrop = useCallback(
    (leadId: string, _fromStageId: string, toStageId: string) => {
      const lead = findLeadInBoard(leadId);
      if (
        backendUser &&
        board?.visibility?.isReadOnly &&
        lead?.assignedTo &&
        lead.assignedTo !== backendUser.id
      ) {
        showError(t('leads.viewMode.readOnly'));
        return;
      }
      moveStage.mutate({ leadId, stageId: toStageId });
    },
    [moveStage.mutate, findLeadInBoard, showError, t, board?.visibility?.isReadOnly, backendUser],
  );

  const isLoading = templatesLoading || boardLoading;
  const hasNoTemplates = !templatesLoading && (!templates || templates.length === 0);
  const hasNoActiveTemplate =
    !templatesLoading && templates && templates.length > 0 && !activeTemplate;

  // Same Yoga collapse as Leads (commit 12534c2): VStack `spacing` (→ gap)
  // wrapping a flex:1 child collapses the flex child to 0 height on iOS.
  // Solution: plain View column, no gap, explicit marginBottom on the title
  // FadeIn to keep vertical rhythm.
  return (
    <View
      testID="funnel_screen"
      style={{
        flex: 1,
        // Match the dashboard tab's content inset. Outer horizontal padding
        // shifts the title and the board together, so the title↔card-title
        // alignment tuned below is preserved.
        paddingHorizontal: tokens.spacing.lg,
        paddingTop: tokens.spacing.lg,
      }}
    >
      <FadeIn
        delay={0}
        translateY={12}
        style={{ marginBottom: isMobile ? tokens.spacing.md : tokens.spacing.lg }}
      >
        <HStack
          justify="between"
          align="center"
          style={{
            flexWrap: 'wrap',
            gap: tokens.spacing.sm,
            // Align with card titles below (and the trailing icons with card
            // title right edges). Card titles sit at column.x + ScrollView pad
            // (8) + card pad-md (12) = 20 from the padded content edge.
            ...(isMobile ? { paddingHorizontal: tokens.spacing.sm + tokens.spacing.md } : {}),
          }}
        >
          <VStack spacing="xs" style={{ flex: 1, minWidth: 120 }}>
            <Text
              variant={isMobile ? 'h5' : 'h4'}
              color={colors.text.primary}
              style={{ fontWeight: '700' }}
            >
              {t('pages.funnel.title')}
            </Text>
            {activeTemplate && (
              <Text variant="caption" color={colors.text.tertiary}>
                {activeTemplate.name}
              </Text>
            )}
          </VStack>

          <HStack spacing="md" align="center">
            {board?.visibility && !isPrivileged && board.visibility.isReadOnly && (
              <Box
                style={{
                  backgroundColor: colors.status.warning.bg,
                  borderRadius: tokens.radius.sm,
                  paddingHorizontal: tokens.spacing.sm,
                  paddingVertical: 2,
                  alignSelf: 'center',
                }}
              >
                <Text variant="caption" color={colors.status.warning.main}>
                  {t('leads.viewMode.readOnly')}
                </Text>
              </Box>
            )}
            {board?.visibility && !isPrivileged && (
              <BoardViewToggle visibility={board.visibility} onViewModeChange={setViewMode} />
            )}

            {board?.visibility && isPrivileged && (
              <OverviewBadge size="sm" style={{ alignSelf: 'center' }}>
                {t(`settings.boardVisibility.${board.visibility.mode}`)}
              </OverviewBadge>
            )}

            <IconButton
              testID="funnel_refresh_button"
              icon={<RefreshCw size={16} color={colors.text.secondary} strokeWidth={2} />}
              variant="ghost"
              size="sm"
              onPress={() => refetch()}
              accessibilityLabel={t('pages.funnel.refreshTooltip')}
              tooltip={t('pages.funnel.refreshTooltip')}
            />
            <Button
              testID="funnel_add_lead_button"
              variant="primary"
              size="sm"
              onPress={() => router.push('/lead/new')}
            >
              <HStack spacing="xs" align="center">
                <Plus size={16} color={colors.background.primary} strokeWidth={2.5} />
                {!isMobile && (
                  <Text
                    variant="bodySmall"
                    color={colors.background.primary}
                    style={{ fontWeight: '500' }}
                  >
                    {t('common.actions.addLead')}
                  </Text>
                )}
              </HStack>
            </Button>
          </HStack>
        </HStack>
      </FadeIn>

      <Box flex={1}>
        {hasNoTemplates && (
          <EmptyState
            icon={<LayoutGrid size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
            title={t('leads.pipeline.noPipelineFound')}
            description={t('leads.pipeline.noPipelineHint')}
            action={{
              label: t('leads.pipeline.goToSettings'),
              onPress: () => router.push('/settings'),
            }}
            dashed
          />
        )}
        {!hasNoTemplates && hasNoActiveTemplate && (
          <EmptyState
            icon={<LayoutGrid size={32} color={colors.text.tertiary} strokeWidth={1.5} />}
            title={t('leads.pipeline.noActivePipeline')}
            description={t('leads.pipeline.noActiveHint')}
            action={{
              label: t('leads.pipeline.goToSettings'),
              onPress: () => router.push('/settings'),
            }}
            dashed
          />
        )}
        {!hasNoTemplates && !hasNoActiveTemplate && (
          <KanbanBoard
            board={board}
            isLoading={isLoading}
            error={boardError}
            onLeadPress={handleLeadPress}
            onDrop={handleDragDrop}
          />
        )}
      </Box>

      {showStageModal && selectedLead && board && (
        <StageMoveModal
          visible={showStageModal}
          onClose={() => {
            setShowStageModal(false);
            setSelectedLead(null);
          }}
          stages={board.stages}
          currentStageId={selectedLead.currentStageId}
          onSelectStage={handleMoveStage}
          loading={moveStage.isPending}
        />
      )}

      <Sheet
        visible={showForceQualifyModal}
        onClose={() => {
          setShowForceQualifyModal(false);
          setBlockedLead(null);
          setForceQualifyReason('');
        }}
        title={t('leads.detail.forceQualify.title')}
        size="sm"
        position="center"
      >
        <VStack testID="force-qualify-modal" spacing="md">
          <Text variant="bodySmall" color={colors.text.secondary}>
            {t('leads.detail.forceQualify.description')}
          </Text>
          <FormInput
            style={forceQualifyStyles.reasonInput}
            placeholder={t('leads.detail.forceQualify.reasonPlaceholder')}
            value={forceQualifyReason}
            onChangeText={setForceQualifyReason}
            multiline
            numberOfLines={3}
          />
          {forceQualify.isError && (
            <Text variant="caption" color={colors.status.error.main}>
              {(forceQualify.error as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || t('leads.detail.forceQualify.error')}
            </Text>
          )}
          <HStack spacing="sm" justify="end">
            <Button
              variant="ghost"
              size="sm"
              onPress={() => {
                setShowForceQualifyModal(false);
                setBlockedLead(null);
                setForceQualifyReason('');
              }}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onPress={handleForceQualifySubmit}
              loading={forceQualify.isPending}
              disabled={!forceQualifyReason.trim()}
            >
              {t('leads.detail.forceQualify.confirm')}
            </Button>
          </HStack>
        </VStack>
      </Sheet>
    </View>
  );
}

const forceQualifyStyles = StyleSheet.create({
  reasonInput: {
    backgroundColor: colors.background.input,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    padding: tokens.spacing.md,
    color: colors.text.primary,
    fontSize: tokens.fontSize.base,
    minHeight: 80,
    textAlignVertical: 'top',
  },
});
